import type { ReactNode } from "react";
import { cn } from "../lib/cn";

/**
 * A section of a page (docs/design/meridian.md, cards 112 and 113): a card with its title, and an optional sentence
 * under it, in a header row whose end holds the section's actions; then the body, padded or `flush` so a table or a
 * list runs to the edges; then an optional footer for the section's own save. `highlight` rings the one the reader
 * acts in. The section is named by its title.
 */
export function Panel({
  title,
  description,
  children,
  id,
  actions,
  footer,
  flush = false,
  highlight = false,
  className,
}: {
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  id?: string | undefined;
  actions?: ReactNode;
  footer?: ReactNode;
  flush?: boolean;
  highlight?: boolean;
  className?: string | undefined;
}) {
  return (
    <section
      aria-label={title}
      id={id}
      className={cn(
        "flex scroll-mt-24 flex-col overflow-hidden rounded-(--radius-card) border border-border bg-surface",
        highlight && "ring-3 ring-brand-50",
        className,
      )}
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border-subtle px-5 py-4">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h3 className="text-subheading font-semibold text-ink">{title}</h3>
          {description !== undefined && <p className="text-small text-ink-muted">{description}</p>}
        </div>
        {actions}
      </header>
      {children !== undefined && (
        <div className={flush ? "flex flex-col" : "flex flex-col gap-4 px-5 py-4 text-body"}>{children}</div>
      )}
      {footer !== undefined && (
        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-border-subtle px-5 py-3">
          {footer}
        </footer>
      )}
    </section>
  );
}
